import { Document } from "@contentful/rich-text-types";
import { flatten, uniqBy } from "lodash";
import { useContext } from "react";
import RichText, { InlineRichText } from "../../components/RichText";
import { QuestionType } from "../../utils/contentful";
import { FurnitureContext } from "../../utils/furniture";
import { QuizContext } from "../../utils/quiz";
import MarkdownText from "../MarkdownText";
import {
    Container,
    Priority,
    PriorityDescriptionContainer,
    PriorityIcon,
    PriorityLabel,
    PriorityLevel,
    Summary,
    TopPriority,
    TopPriorityIcon,
    TopPriorityLevel,
} from "./styles";

type PriorityResultProps = {
    result: any;
};

export type PriorityOption = {
    _id: string;
    body: string;
    bodyRich: any;
    priorityIcon: {
        url: string;
    };
    priorityDescription?: string;
    priorityDescriptionRich?: Document;
    score?: number;
    percent?: number;
};

const PrioritiesResult = ({ result }: PriorityResultProps) => {
    const { getOptions, logicState, quizState } = useContext(QuizContext);
    const windowWidth = 800;
    const furniture = useContext(FurnitureContext);
    const { availableQuestions } = quizState;
    const priorityQuestions = availableQuestions.filter(
        q => q.type === QuestionType.Priority,
    );
    const totalComparisons = priorityQuestions.length;
    const percentageDivisor = totalComparisons * 100;
    const priorities: PriorityOption[] = uniqBy(
        flatten(
            priorityQuestions.map(q => {
                return getOptions(null, q);
            }),
        ),
        "_id",
    );
    // Get total score from local internal state by option ID
    priorities.forEach((o: PriorityOption) => {
        const id = o._id;
        const score = logicState.internal.local[id];
        o.score = score;
        // calculate display score
        o.percent = Math.round(100 * (score / percentageDivisor));
    });

    // if all scores are the same, there is no winner, and no top priority
    const hasNoWinner = priorities.every(
        (p: PriorityOption) => p.score === priorities[0].score,
    );

    // sort priorities
    priorities.sort((a, b) => {
        return b.score - a.score;
    });

    const topPriority = hasNoWinner ? null : priorities.shift();

    // const getNumberAnswered: (values: {
    // 	answers: number;
    // 	total: number;
    // }) => string = template(furniture.strings.quizReviewLabelNumberAnswered);

    // const prefix = getNumberAnswered({
    // 	answers: quizState.questionDisplayCount,
    // 	total: quizState.questionDisplayCount,
    // });
    const summary = topPriority
        ? result.defaultTitle
        : furniture.strings.quizReviewLabelAllFactorsEqual;
    const fallbackDescription = result.fallbackDescription;
    const priorityDescription = topPriority?.priorityDescription;

    const priorityDescriptionRich = topPriority?.priorityDescriptionRich;

    const renderPriority = (priority: PriorityOption) => {
        const optionBody = priority.bodyRich ? (
            <InlineRichText doc={priority.bodyRich} />
        ) : (
            priority.body
        );

        const imageUrl =
            `${priority.priorityIcon?.url}?w=${windowWidth}` &&
            `https:${priority.priorityIcon.url}?w=${windowWidth}`;
        return (
            <Priority key={priority._id}>
                <PriorityIcon source={{ uri: imageUrl }} />
                <PriorityLabel allowFontScaling={false}>
                    {optionBody}
                </PriorityLabel>
                <PriorityLevel type="body1" allowFontScaling={false}>
                    {`${priority.percent}%`}
                </PriorityLevel>
            </Priority>
        );
    };

    const renderTopPriority = (priority: PriorityOption) => {
        const optionBody = priority.bodyRich ? (
            <InlineRichText doc={priority.bodyRich} />
        ) : (
            priority.body
        );
        const imageUrl =
            `${priority.priorityIcon?.url}?w=${windowWidth}` &&
            `https:${priority.priorityIcon.url}?w=${windowWidth}`;
        return (
            <TopPriority>
                <TopPriorityIcon source={{ uri: imageUrl }} />
                <TopPriorityLevel type="body1" allowFontScaling={false}>
                    {`${priority.percent}%`}
                </TopPriorityLevel>
                <PriorityLabel allowFontScaling={false}>
                    {optionBody}
                </PriorityLabel>
            </TopPriority>
        );
    };

    return (
        <Container>
            {/* <Prefix type="cta2">{prefix}</Prefix> */}
            <Summary type="h5">{summary}</Summary>
            {topPriority && renderTopPriority(topPriority)}
            <PriorityDescriptionContainer>
                <PriorityDescription
                    fallback={hasNoWinner}
                    defaultDescription={fallbackDescription}
                    markdownDescription={priorityDescription}
                    richDescription={priorityDescriptionRich}
                />
            </PriorityDescriptionContainer>
            {topPriority ? (
                <Summary type="h4">
                    {furniture.strings.quizReviewLabelOtherPriorities}
                </Summary>
            ) : null}
            {priorities.map((priority: PriorityOption) =>
                renderPriority(priority),
            )}
        </Container>
    );
};

export default PrioritiesResult;

const PriorityDescription = ({
    fallback,
    defaultDescription,
    markdownDescription,
    richDescription,
}) => {
    if (fallback) {
        return <MarkdownText>{defaultDescription}</MarkdownText>;
    }
    return richDescription ? (
        <RichText doc={richDescription} />
    ) : (
        <MarkdownText>{markdownDescription}</MarkdownText>
    );
};
